import React from "react";
import styled from "@emotion/styled";
import * as Tooltip from "@radix-ui/react-tooltip";

export type TooltipComponentProps = {
  // Trigger
  children: React.ReactNode;

  // Content + Arrow
  content?: React.ReactNode | (() => React.ReactNode);
  "aria-label"?: string | undefined;
  side?: "top" | "right" | "bottom" | "left";
  variant?: "default" | "dark";
  contentMaxWidth?: number;

  // Provider
  delayDuration?: number;

  // Root
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

/**
 * @param children - Trigger element
 * @param content - Tooltip content
 * @param aria-label - Aria label for the tooltip content, if not set content is used **meaning it's rendered twice**
 * @param side - Position of the tooltip relative to the trigger
 * @param variant - Style variant of the tooltip
 * @param contentMaxWidth - Maximum width of the tooltip content
 * @param delayDuration - Delay before the tooltip appears
 * @param open - Controlled open state of the tooltip, if not set state is managed internally
 * @param onOpenChange - Callback when the open state changes
 */
function TooltipComponent({
  children,
  content,
  "aria-label": ariaLabel,
  contentMaxWidth = 290,
  side = "top",
  variant = "default",
  delayDuration = 700,
  open,
  onOpenChange,
}: TooltipComponentProps) {
  let contentToRender: React.ReactNode | undefined;

  if (typeof content === "function") {
    contentToRender = content();
  } else {
    contentToRender = content;
  }

  if (contentToRender == null) {
    return <>{children}</>;
  }

  return (
    <Tooltip.Provider delayDuration={delayDuration}>
      <Tooltip.Root open={open} onOpenChange={onOpenChange}>
        <Tooltip.Trigger asChild>{children}</Tooltip.Trigger>
        <Wrapper>
          <StyledContent
            maxWidth={contentMaxWidth}
            aria-label={ariaLabel}
            sideOffset={5}
            side={side}
            variant={variant}
          >
            {contentToRender}
            <StyledArrow variant={variant} />
          </StyledContent>
        </Wrapper>
      </Tooltip.Root>
    </Tooltip.Provider>
  );
}

export default TooltipComponent;

// Tooltip.Content has an unstylable div we need to override, so doing thishttps://github.com/radix-ui/primitives/issues/2908
const Wrapper = styled.div({
  position: "fixed",
  zIndex: 9999,
  "& > [data-radix-popper-content-wrapper]": {
    all: "initial",
  },
});

const StyledContent = styled(Tooltip.Content, {
  shouldForwardProp: (prop) => !["maxWidth"].includes(prop as string),
})<{ maxWidth: number; variant: "default" | "dark" }>(({ theme, maxWidth, variant }) => ({
  maxWidth,
  whiteSpace: "pre-wrap",
  fontFamily: theme.fontFamily,
  fontSize: "14px",
  ...(variant === "default" && {
    backgroundColor: theme.colors.sysBackgroundDefault,
    padding: "16px",
    borderRadius: "8px",
    boxShadow: "0px 2px 8px 0px rgba(0, 0, 0, 0.12)",
  }),
  ...(variant === "dark" && {
    display: "flex",
    padding: "12px",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: "4px",
    alignSelf: "stretch",
    borderRadius: "4px",
    background: "#2D3B54",
    color: theme.colors.sysTextWhite,
  }),
}));

const StyledArrow = styled(Tooltip.Arrow)<{ variant: "default" | "dark" }>(
  ({ theme, variant }) => ({
    fill: variant === "default" ? theme.colors.sysBackgroundDefault : "#2D3B54",
    width: "28px",
    height: "14px",
  }),
);
